import { prefersReducedMotion, yieldToMainThread } from '@theme/utilities';

const HERO_TITLE_SELECTOR = '.hero-wrapper:first-child .text-block.h1 h1';

/**
 * @param {Text} textNode
 * @param {{ value: number }} charIndex
 * @returns {DocumentFragment}
 */
function wrapTextNode(textNode, charIndex) {
  const fragment = document.createDocumentFragment();
  const text = textNode.textContent ?? '';

  for (const char of text) {
    const span = document.createElement('span');
    span.className = 'hero-title-reveal__char';

    if (char === ' ') {
      span.classList.add('hero-title-reveal__char--space');
      span.textContent = '\u00a0';
    } else {
      span.textContent = char;
    }

    span.style.setProperty('--char-index', String(charIndex.value++));
    fragment.appendChild(span);
  }

  return fragment;
}

/**
 * @param {HTMLElement} element
 * @param {{ value: number }} charIndex
 * @returns {DocumentFragment}
 */
function processChildNodes(element, charIndex) {
  const fragment = document.createDocumentFragment();

  for (const node of element.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      fragment.appendChild(wrapTextNode(/** @type {Text} */ (node), charIndex));
    } else if (node.nodeName === 'BR') {
      fragment.appendChild(document.createElement('br'));
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const wrapper = /** @type {HTMLElement} */ (node).cloneNode(false);
      wrapper.appendChild(processChildNodes(/** @type {HTMLElement} */ (node), charIndex));
      fragment.appendChild(wrapper);
    }
  }

  return fragment;
}

/**
 * @param {HTMLHeadingElement} heading
 */
function initHeroTitleReveal(heading) {
  if (heading.dataset.heroTitleReveal === 'true') return;

  const accessibleText = heading.textContent?.replace(/\s+/g, ' ').trim();
  if (accessibleText) {
    heading.setAttribute('aria-label', accessibleText);
  }

  if (prefersReducedMotion()) {
    heading.dataset.heroTitleReveal = 'true';
    heading.classList.add('hero-title-reveal--static');
    return;
  }

  const charIndex = { value: 0 };
  const content = processChildNodes(heading, charIndex);

  heading.textContent = '';
  heading.appendChild(content);
  heading.classList.add('hero-title-reveal');
  heading.dataset.heroTitleReveal = 'true';

  requestAnimationFrame(() => {
    heading.classList.add('hero-title-reveal--ready');

    requestAnimationFrame(() => {
      heading.classList.add('hero-title-reveal--active');
    });
  });
}

function setup() {
  const heading = document.querySelector(HERO_TITLE_SELECTOR);

  if (!(heading instanceof HTMLHeadingElement)) return;

  initHeroTitleReveal(heading);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    yieldToMainThread().then(setup);
  });
} else {
  yieldToMainThread().then(setup);
}

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof HTMLElement)) return;

  const heading = event.target.querySelector(HERO_TITLE_SELECTOR);

  if (heading instanceof HTMLHeadingElement) {
    yieldToMainThread().then(() => initHeroTitleReveal(heading));
  }
});
